import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { WebUserReference } from '../../models/web-user';
import { AdminUserReference } from '../../models/admin-user';
import {
    UniqueRequestMessage,
    UniqueRequestMessageOrigin,
} from '../../models/unique-request';
import { Divider } from '@material-ui/core';
import EditIcon from '@mui/icons-material/Edit';
import { UniqueRequestMessageEditDialog } from './unique-request-message-edit-dialog';
import { WorkStatusType } from '../../models/work-status';
import { useState } from 'react';

interface UniqueRequestMessageShowProps {
    status: WorkStatusType;
    sender: WebUserReference | AdminUserReference | null;
    message: UniqueRequestMessage;
}

export const UniqueRequestMessageShow = ({
    status,
    sender,
    message,
}: UniqueRequestMessageShowProps) => {
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const isIncoming = message.origin === UniqueRequestMessageOrigin.Web;

    return (
        <Box
            key={message.id}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isIncoming ? 'flex-start' : 'flex-end',
            }}
        >
            <Box
                display={'flex'}
                justifyContent={'space-between'}
                paddingLeft={1}
                paddingRight={1}
                sx={{
                    width: '95%',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color:
                            theme.palette.mode === 'dark'
                                ? theme.palette.grey['200']
                                : theme.palette.grey['600'],
                    }}
                >
                    {message.createdAt
                        ? new Date(message.createdAt).toLocaleString('hu-HU')
                        : ''}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color:
                            theme.palette.mode === 'dark'
                                ? theme.palette.grey['200']
                                : theme.palette.grey['600'],
                    }}
                >
                    {message.origin === UniqueRequestMessageOrigin.Admin
                        ? message.read
                            ? 'Látta'
                            : 'Olvasatlan'
                        : ''}
                </Typography>
            </Box>
            <Box
                sx={{
                    width: '95%',
                    padding: 2,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? isIncoming
                                ? theme.palette.grey['300']
                                : theme.palette.primary['300']
                            : isIncoming
                              ? theme.palette.grey['100']
                              : theme.palette.primary['100'],
                    border: 1,
                    borderRadius: 1,
                    borderColor:
                        theme.palette.mode === 'dark'
                            ? isIncoming
                                ? theme.palette.grey['400']
                                : theme.palette.primary['400']
                            : isIncoming
                              ? theme.palette.grey['200']
                              : theme.palette.primary['200'],
                    color: theme.palette.grey['800'],
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: 1,
                        marginBottom: 1,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        {message.messageCaseNumber}
                    </Typography>
                    {message.origin == UniqueRequestMessageOrigin.Admin && (
                        <Box>
                            <Tooltip
                                title="A felhasználó már látta ezt az üzenetet."
                                disableHoverListener={!message.read}
                            >
                                <span>
                                    <Button
                                        variant={'contained'}
                                        size={'small'}
                                        sx={{ minWidth: 0, padding: 0.5 }}
                                        onClick={() => setDialogOpen(true)}
                                        disabled={
                                            message.read ||
                                            status === WorkStatusType.Closed
                                        }
                                    >
                                        <EditIcon fontSize={'small'} />
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                {sender && (
                    <Typography
                        variant="caption"
                        sx={{
                            color:
                                theme.palette.mode === 'dark'
                                    ? theme.palette.grey['700']
                                    : theme.palette.grey['600'],
                            display: 'block',
                            marginBottom: 1,
                        }}
                    >
                        {isIncoming ? 'Felhasználó' : 'Admin'}:{' '}
                        {sender.name ?? 'Törölt felhasználó'} ({sender.email})
                    </Typography>
                )}
                <Divider />
                <Typography variant="body2" marginTop={1} marginBottom={1}>
                    {message.content}
                </Typography>
                {message.attachments?.length > 0 && (
                    <>
                        <Divider />
                        <Typography
                            variant="caption"
                            sx={{
                                color:
                                    theme.palette.mode === 'dark'
                                        ? theme.palette.grey['700']
                                        : theme.palette.grey['600'],
                                display: 'block',
                                marginTop: 1,
                            }}
                        >
                            Csatolmányok: {message.attachments.length}
                        </Typography>
                    </>
                )}
                {message?.attachments?.map((att: any) => {
                    return (
                        <Box marginTop={1} fontSize={14}>
                            {att.name}:{' '}
                            <a
                                href={att.url}
                                target={'_blank'}
                                style={{
                                    color:
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary['800']
                                            : theme.palette.primary.main,
                                }}
                            >
                                {att.url}
                            </a>
                        </Box>
                    );
                })}
            </Box>
            <UniqueRequestMessageEditDialog
                message={message}
                open={dialogOpen}
                setOpen={setDialogOpen}
            />
        </Box>
    );
};
